import React, { useEffect, useState } from "react";
import { useCompanion } from "@/companion/CompanionContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2, Scale, ClipboardCheck } from "lucide-react";
import { celebrateSigned } from "@/lib/celebrate";
import PawMark from "@/components/PawMark";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  coaching: "Coaching & Counseling",
  verbal: "Verbal Warning",
  written: "Written Warning",
  final: "Final Warning",
  termination: "Termination",
};
const ISSUE_LABELS = {
  absenteeism: "Absenteeism",
  policy_violation: "Policy / procedure violation",
  conduct: "Conduct",
  unsatisfactory_performance: "Unsatisfactory performance",
  safety_violation: "Safety violation",
  other: "Other",
};
const REVIEW_LABELS = { "30_day": "30-day", "60_day": "60-day", "90_day": "90-day", annual: "Annual", custom: "Custom" };

export default function PublicWitness() {
  const companion = useCompanion();
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, err: null, data: null });
  const [typedName, setTypedName] = useState("");
  const [signed, setSigned] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BASE_URL}/api/public/witness/${token}`)
      .then((r) => setState({ loading: false, err: null, data: r.data }))
      .catch((e) => setState({ loading: false, err: e.response?.data?.detail || "Invalid or expired link", data: null }));
  }, [token]);

  const submit = async () => {
    if (!typedName.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${BASE_URL}/api/public/witness/${token}/sign`, { typed_name: typedName.trim() });
      setSigned(true);
      setTimeout(() => celebrateSigned(), 150);
    } catch (e) {
      alert(e.response?.data?.detail || "Signing failed");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 size={20} className="animate-spin text-stone-400" />
      </div>
    );
  }
  if (state.err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 p-8 text-center">
        <AlertTriangle size={32} className="text-amber-500 mb-3" />
        <div className="font-serif-display text-2xl text-stone-900 mb-2">Link not available</div>
        <div className="text-sm text-stone-600 max-w-md">{state.err}</div>
      </div>
    );
  }

  const { witness_name, witness_boutique, role = "witness", kind, record, already_signed } = state.data;
  const isDone = signed || already_signed;
  const Icon = kind === "discipline" ? Scale : ClipboardCheck;
  const roleLabel = { witness: "witness", employee: "employee", mgmt: "manager" }[role] || "witness";
  const introVerb = role === "employee" ? "acknowledge" : role === "mgmt" ? "authorize" : "witness";

  return (
    <section className="min-h-screen bg-stone-50 flex flex-col items-center py-12 px-4" data-testid="witness-page" style={{ background: "var(--bg-main, #F7F4EE)" }}>
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-8">
          <PawMark size={16} />
          <span className="font-serif-display text-lg text-stone-900">{companion.productWordmark}</span>
          <span className="text-[10px] uppercase tracking-widest text-stone-400 ml-2">{roleLabel} signing</span>
        </div>

        <div className="bg-white rounded-3xl subtle-border card-shadow overflow-hidden">
          <div className="px-8 py-6 border-b border-stone-100">
            <div className="flex items-start gap-3">
              <Icon size={20} className="text-stone-700 mt-1" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-400">
                  {kind === "discipline" ? "Disciplinary Action" : "Employee Review"}
                </div>
                <div className="font-serif-display text-2xl text-stone-900">
                  {kind === "discipline" ? ACTION_LABELS[record.action_type] : `${REVIEW_LABELS[record.review_type]} review`}
                </div>
                <div className="text-sm text-stone-500 mt-1">
                  For <span className="text-stone-800">{record.employee_name}</span> · {record.boutique_code}
                </div>
              </div>
            </div>
          </div>

          <div className="px-8 py-6 space-y-4">
            <p className="text-sm text-stone-700 leading-relaxed" data-testid="witness-intro">
              You are being asked to {introVerb} this{" "}
              <strong>{kind === "discipline" ? "disciplinary action" : "employee review"}</strong>{" "}
              as <span className="font-medium">{witness_name}</span>
              {witness_boutique ? ` from ${witness_boutique}` : ""}. The full record is below for
              your review. Signing confirms that you {role === "employee" ? "have read and understood" : role === "mgmt" ? "authorize" : "observed"} the record and its contents.
            </p>

            <div className="rounded-2xl bg-stone-50 border border-stone-200 p-5 space-y-3 text-sm">
              {kind === "discipline" ? (
                <>
                  <Row label="Issue" value={ISSUE_LABELS[record.issue_category]} />
                  {record.sop_reference && <Row label="SOP" value={record.sop_reference} />}
                  <Row label="Occurred" value={`${record.incident_date}${record.incident_time ? " · " + record.incident_time : ""}`} />
                  <Block label="Explanation" value={record.explanation} />
                  <Block label="Goals / corrective behavior" value={record.corrective_goals} />
                  {record.next_step && <Row label="Next step if not corrected" value={record.next_step} />}
                </>
              ) : (
                <>
                  <Row label="Type" value={REVIEW_LABELS[record.review_type]} />
                  <Row label="Due" value={record.due_date} />
                  {record.template_notes && <Block label="Notes" value={record.template_notes} />}
                  {record.responses && (
                    <>
                      <Block label="Strengths" value={record.responses.strengths} />
                      <Block label="Growth areas" value={record.responses.growth_areas} />
                      <Block label="Goals" value={record.responses.goals} />
                    </>
                  )}
                </>
              )}
              <div className="pt-2 border-t border-stone-200 text-[11px] text-stone-500">
                Logged by {record.logged_by_name || record.created_by_name || "—"} on {(record.logged_at || record.created_at || "").slice(0, 10)}
              </div>
            </div>

            {isDone ? (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 flex items-center gap-3" data-testid="witness-signed">
                <CheckCircle2 size={22} className="text-emerald-700" />
                <div>
                  <div className="font-medium text-emerald-900">Thank you — {roleLabel} signature received.</div>
                  <div className="text-xs text-emerald-800 mt-0.5">You may close this window.</div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-stone-300 bg-white p-5 space-y-3" data-testid="witness-sign-panel">
                <div className="text-[11px] uppercase tracking-widest text-stone-500">Type your full name to sign as {roleLabel}</div>
                <input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder={witness_name}
                  data-testid="witness-name-input"
                  className="w-full px-4 py-2.5 rounded-2xl subtle-border text-base font-serif italic outline-none focus:border-stone-500"
                />
                <button
                  onClick={submit}
                  disabled={busy || !typedName.trim()}
                  data-testid="witness-sign-btn"
                  className="w-full px-5 py-3 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40"
                >
                  {busy ? "Signing…" : "Sign as witness"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-[11px] text-stone-400 italic max-w-md mx-auto">
          Signing captures your typed name, timestamp, and the fact that you viewed this full record.
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="label-eyebrow w-40 shrink-0">{label}</div>
      <div className="text-stone-800">{value}</div>
    </div>
  );
}
function Block({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="text-stone-800 whitespace-pre-wrap leading-relaxed">{value}</div>
    </div>
  );
}
